import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastService } from 'ng-uikit-pro-standard';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AddToCartWithAttributesPayload, Attributes } from 'src/app/shared-core/models/cart-data.model';
import { CartService } from 'src/app/shared-core/services/cart.service';
import { createTypicalEmailValidator } from 'src/app/shared-core/validators/typical-email-validator';

@Component({
  selector: 'app-gift-card-cart-customization',
  templateUrl: './gift-card-cart-customization.component.html',
  styleUrls: ['./gift-card-cart-customization.component.scss']
})
export class GiftCardCartCustomizationComponent implements AfterViewInit {
  @Output() public hidden = new EventEmitter();
  
  public formGroup = new FormGroup({
    recipientName: new FormControl('', [Validators.maxLength(50)]),
    recipientEmail: new FormControl('', [Validators.maxLength(50), createTypicalEmailValidator()]),
    giftCardValue: new FormControl(25, [Validators.required]),
    customValue: new FormControl(null),
    customMessage: new FormControl('', [Validators.maxLength(250)]),
  });

  public priceValues = [25, 50, 75, 100, 200, 250, 500];

  private _isSaving = false;

  get isSubmitDisabled(): boolean {
    return this.formGroup.invalid || this._isSaving;
  }

  get showCustomValueField(): boolean {
    return this.formGroup.get('giftCardValue').value === 0;
  }

  get isDigitalGiftCard(): boolean {
    return this._webCode.startsWith('EGIFTVAR');
  }

  constructor(
    @Inject('webCode') private _webCode: string,
    @Inject('recordId') private _recordId: string,
    @Inject('itemAttributes') private _itemAttributes: Attributes,
    private _cartService: CartService,
    private _toaster: ToastService,
    private _changeDetectorRef: ChangeDetectorRef) { }

  ngAfterViewInit(): void {
    if (this.isDigitalGiftCard) {
      this.addValidatorsForDigitalGiftCards();
    }

    this.formGroup.get('giftCardValue').valueChanges.subscribe(() => {
      const control = this.formGroup.get('customValue');

      if (this.showCustomValueField) {
        control.addValidators([Validators.required, Validators.min(25), Validators.max(1000), Validators.pattern(/^\d+$/)])
      } else {
        control.clearValidators();
      }

      control.updateValueAndValidity();
    });

    if (this._itemAttributes) {
      this.formGroup.patchValue({
        recipientName: this._itemAttributes.GiftCardRecipientName,
        recipientEmail: this._itemAttributes.GiftCardRecipientEmail,
        customMessage: this._itemAttributes.GiftCardMessage,
        giftCardValue: this.priceValues.some(x => x === this._itemAttributes.GiftCardPrice) ? this._itemAttributes.GiftCardPrice : 0,
        customValue: this.priceValues.some(x => x === this._itemAttributes.GiftCardPrice) ? null : this._itemAttributes.GiftCardPrice
      });
    }

    this.formGroup.markAllAsTouched();
    this._changeDetectorRef.detectChanges();
  }

  private addValidatorsForDigitalGiftCards() {
    const recipientNameControl = this.formGroup.get('recipientName');
    recipientNameControl.addValidators(Validators.required);
    recipientNameControl.updateValueAndValidity();

    const recipientEmailControl = this.formGroup.get('recipientEmail');
    recipientEmailControl.addValidators(Validators.required);
    recipientEmailControl.updateValueAndValidity();
  }

  public hide(addedToCart: boolean, updateEvent: boolean): void {
    this.hidden.emit({ addedToCart, updateEvent});
  }

  get isUpdate(): boolean {
    if (this._recordId)
      return true;
    else
      return false;
  }

  public submit(): void {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }

    const amountControl = this.showCustomValueField ?
      this.formGroup.get('customValue') :
      this.formGroup.get('giftCardValue');

    const attributes = {
      ...this._itemAttributes,
      giftCardRecipientName: this.formGroup.get('recipientName').value,
      giftCardRecipientEmail: this.formGroup.get('recipientEmail').value,
      giftCardPrice: amountControl.value,
      giftCardMessage: this.formGroup.get('customMessage').value
    } as Attributes;

    this._isSaving = true;
    if (this._recordId) {
      this._cartService.updateGiftCardData(this._recordId, attributes).pipe(catchError(err => {

        this._isSaving = false;
        this._toaster.error('An error occured while updating gift card. Please try again.', 'Unable to update gift card');
        return of(null);

      })).subscribe((result) => {
        this.hide(true, true);
        setTimeout(() => this._isSaving = false, 1000);
      });
      return;
    }

    const payload = {
      type: 'ShoppingCart',
      source: 'Resource',
      webCode: this._webCode,
      quantity: 1,
      attributes: attributes
    } as AddToCartWithAttributesPayload

    this._cartService.addToCartWithAttributes(payload).pipe(catchError(err => {

      this._isSaving = false;
      this._toaster.error('An error occured adding to cart. Please try again.', 'Unable to add to cart');
      return of(null);

    })).subscribe((item) => {

      if (!item) {
        this._toaster.error('An error occured adding to cart. Please try again.', 'Unable to add to cart');
        return;
      }

      this.hide(true, false);
      setTimeout(() => this._isSaving = false, 1000);

    });
  }
}

